import { Table, Button, Modal, Input, Tag, message, Typography } from "antd";
import { useState, useContext, useEffect, useCallback } from "react";
import dayjs from "dayjs";
import axios from "axios";
import { AuthContext } from "../../context/AuthContext";
import { NavigationContext } from "../../context/NavigationContext";
import { CheckOutlined, CloseOutlined, SearchOutlined, DownloadOutlined } from "@ant-design/icons";
import * as XLSX from "xlsx";

const { Title } = Typography;

export default function TlChange() {
    const { authData, backUrl } = useContext(AuthContext);
    const { navigationParams, clearNavigationParams } = useContext(NavigationContext);
    const [requests, setRequests] = useState([]);
    const [filters, setFilters] = useState({ status: "PENDING", year: null });
    const [counts, setCounts] = useState({ PENDING: 0, APPROVED: 0, REJECTED: 0 });
    const [searchText, setSearchText] = useState("");

    const [isApproveModalVisible, setIsApproveModalVisible] = useState(false);
    const [isRejectModalVisible, setIsRejectModalVisible] = useState(false);
    const [adminRemarks, setAdminRemarks] = useState("");
    const [selectedRecord, setSelectedRecord] = useState(null);

    const [isSubsModalVisible, setIsSubsModalVisible] = useState(false);
    const [subsRecord, setSubsRecord] = useState(null);
    const [loading, setLoading] = useState(false);

    // ⚡ Fetch TL transfer requests
    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (filters.status) params.append("status", filters.status);
            if (searchText.trim()) params.append("search", searchText.trim());
            
            const res = await axios.get(`${backUrl}/api/emp/tl-transfer/list/?${params.toString()}`, {
                headers: { Authorization: `Bearer ${authData?.access}` },
            });
            setRequests(res.data.results || res.data || []);
        } catch (error) {
            console.error("Error fetching TL transfer requests:", error);
            message.error("Failed to load TL transfer requests.");
        } finally {
            setLoading(false);
        }
    }, [backUrl, authData, filters.status, searchText]);

    // ⚡ Fetch Status Counts
    const fetchCounts = async () => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/tl-transfer-status-counts/`, {
                headers: { Authorization: `Bearer ${authData?.access}` }
            });
            const countsMap = { PENDING: 0, APPROVED: 0, REJECTED: 0 };
            res.data.forEach(item => {
                countsMap[item.title.toUpperCase()] = item.count;
            });
            setCounts(countsMap);
        } catch (err) {
            console.error("Failed to fetch TL status counts:", err);
        }
    };

    // Sync with NavigationContext
    useEffect(() => {
        if (navigationParams?.status) {
            setFilters(prev => ({ ...prev, status: navigationParams.status.toUpperCase() }));
            clearNavigationParams();
        }
    }, [navigationParams, clearNavigationParams]);

    useEffect(() => {
        fetchData();
        fetchCounts();
    }, [fetchData, filters.status, filters.year]);

    const statusColors = {
        PENDING: "orange",
        APPROVED: "green",
        REJECTED: "red",
    };

    // Handle Approve
    const handleApprove = async () => {
        if (!selectedRecord?.id) return;
        try {
            const res = await axios.post(
                `${backUrl}/api/emp/tl-replacement/approve/${selectedRecord.id}/`,
                { action: "APPROVE", approver_reason: adminRemarks },
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            message.success(res.data?.message || "TL transfer request approved successfully.");
            setIsApproveModalVisible(false);
            setAdminRemarks("");
            setSelectedRecord(null);
            await fetchData();
            await fetchCounts();
        } catch (error) {
            console.error("Error approving request:", error);
            message.error(error.response?.data?.message || "Failed to approve request.");
        }
    };

    // Handle Reject
    const handleReject = async () => {
        if (!selectedRecord?.id) return;
        try {
            const res = await axios.post(
                `${backUrl}/api/emp/tl-replacement/approve/${selectedRecord.id}/`,
                { action: "REJECT", reason: adminRemarks },
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            message.success(res.data?.message || "TL transfer request rejected.");
            setIsRejectModalVisible(false);
            setAdminRemarks("");
            setSelectedRecord(null);
            await fetchData();
            await fetchCounts();
        } catch (error) {
            console.error("Error rejecting request:", error);
            message.error(error.response?.data?.message || "Failed to reject request.");
        }
    };

    const isSuperuser = authData?.employee?.is_superuser || authData?.user?.is_superuser || authData?.is_superuser;
    const isPendingTab = (filters.status || "PENDING").toUpperCase() === "PENDING";

    const columns = [
        {
            title: "Outgoing TL",
            key: "outgoing_tl",
            render: (_, record) => record.outgoing_tl_name || record.outgoing_tl || "-",
        },
        {
            title: "Incoming TL",
            key: "incoming_tl",
            render: (_, record) => record.incoming_tl_name || record.incoming_tl || "-",
        },
        {
            title: "Sync HOD",
            dataIndex: "sync_hod",
            key: "sync_hod",
            render: (val) => (val ? <Tag color="blue">Yes</Tag> : <Tag color="default">No</Tag>),
        },
        {
            title: "Requested By",
            key: "requesting_admin",
            render: (_, record) => record.requesting_admin_name || record.requesting_admin || "-",
        },
        {
            title: "Transferred Employees",
            key: "target_employees",
            render: (_, record) => {
                const employees = record.target_employees || [];
                const count = record.total_count ?? employees.length;
                if (count === 0) return "-";
                return (
                    <Button
                        style={{ padding: 0 }}
                        type="link"
                        onClick={() => {
                            setSubsRecord(record);
                            setIsSubsModalVisible(true);
                        }}
                    >
                        ({count}) employees
                    </Button>
                );
            },
        },
        {
            title: "Requested On",
            dataIndex: "created_at",
            key: "created_at",
            render: (date) => (date ? dayjs(date).format("DD/MM/YYYY") : "-"),
        },
        {
            title: "Approved On",
            dataIndex: "processed_at",
            key: "processed_at",
            render: (date) => (date ? dayjs(date).format("DD/MM/YYYY") : "-"),
        },
        {
            title: "Remarks",
            key: "approver_reason",
            render: (_, record) => record.approver_reason || "-",
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (text) => (
                <Tag color={statusColors[text] || "default"}>{text || "-"}</Tag>
            ),
        },
        ...(isSuperuser && isPendingTab
            ? [{
                title: "Actions",
                key: "actions",
                render: (_, record) => (
                    <div style={{ display: "flex", gap: "8px" }}>
                        <Button
                            type="text"
                            style={{ color: "green" }}
                            icon={<CheckOutlined />}
                            onClick={() => {
                                setSelectedRecord(record);
                                setIsApproveModalVisible(true);
                            }}
                        />
                        <Button
                            type="text"
                            danger
                            icon={<CloseOutlined />}
                            onClick={() => {
                                setSelectedRecord(record);
                                setIsRejectModalVisible(true);
                            }}
                        />
                    </div>
                ),
            }]
            : []),
    ];

    const filteredData = requests.filter((item) => {
        const date = dayjs(item.created_at);
        const matchYear = filters.year ? date.year() === filters.year : true;
        return matchYear;
    });

    const handleExportToExcel = () => {
        if (!filteredData || filteredData.length === 0) {
            message.warning("No data available to export.");
            return;
        }

        const exportRows = filteredData.map((record, index) => {
            const targetEmps = record.target_employees || [];
            const empCount = record.total_count ?? (Array.isArray(targetEmps) ? targetEmps.length : 0);

            let transferredEmpsStr = "-";
            if (Array.isArray(targetEmps) && targetEmps.length > 0) {
                transferredEmpsStr = targetEmps
                    .map((emp) => `${emp.first_name || ''} ${emp.last_name || ''} (${emp.emp_code || '-'})`.trim())
                    .join("; ");
            } else if (empCount > 0) {
                transferredEmpsStr = `${empCount} Employee(s)`;
            }

            return {
                "S. No.": index + 1,
                "Request ID": record.id || "-",
                "Outgoing TL": record.outgoing_tl_name || record.outgoing_tl || "-",
                "Incoming TL": record.incoming_tl_name || record.incoming_tl || "-",
                "Sync HOD": record.sync_hod ? "Yes" : "No",
                "Requested By": record.requesting_admin_name || record.requesting_admin || "-",
                "Transferred Employees Count": empCount,
                "Transferred Employees": transferredEmpsStr,
                "Requested On": record.created_at ? dayjs(record.created_at).format("DD/MM/YYYY HH:mm") : "-",
                "Approved On": record.processed_at ? dayjs(record.processed_at).format("DD/MM/YYYY HH:mm") : "-",
                "Status": record.status || "-",
                "Remarks": record.approver_reason || "-",
            };
        });

        const worksheet = XLSX.utils.json_to_sheet(exportRows);
        worksheet["!cols"] = [
            { wch: 8 },  // S. No.
            { wch: 12 }, // Request ID
            { wch: 25 }, // Outgoing TL
            { wch: 25 }, // Incoming TL
            { wch: 12 }, // Sync HOD
            { wch: 25 }, // Requested By
            { wch: 26 }, // Transferred Employees Count
            { wch: 45 }, // Transferred Employees
            { wch: 18 }, // Requested On
            { wch: 18 }, // Approved On
            { wch: 12 }, // Status
            { wch: 30 }, // Remarks
        ];

        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "TL Change Requests");

        const statusLabel = filters.status ? filters.status.toLowerCase() : "all";
        const dateStr = dayjs().format("YYYY-MM-DD");
        XLSX.writeFile(workbook, `TL_Change_Requests_${statusLabel}_${dateStr}.xlsx`);
        message.success("Exported TL change requests to Excel successfully!");
    };

    return (
        <>
            <Title level={4} style={{ 
                color: "#0e497b", 
                marginBottom: 20, 
                textAlign: "center",
                textTransform: "uppercase" 
            }}>
                Change TL Requests
            </Title>
            
            {/* Status Tabs */}
            <div style={{ textAlign: "center", marginBottom: "20px" }}>
                <Button
                    type={filters.status === "PENDING" ? "primary" : "default"}
                    onClick={() => setFilters(prev => ({ ...prev, status: "PENDING" }))}
                    style={{
                        marginRight: "10px",
                        backgroundColor: filters.status === "PENDING" ? "#faad14" : undefined,
                        borderColor: filters.status === "PENDING" ? "#faad14" : undefined,
                        color: filters.status === "PENDING" ? "white" : undefined,
                        minWidth: "120px"
                    }}
                >
                    Pending ({counts.PENDING})
                </Button>
                <Button
                    type={filters.status === "APPROVED" ? "primary" : "default"}
                    onClick={() => setFilters(prev => ({ ...prev, status: "APPROVED" }))}
                    style={{
                        marginRight: "10px",
                        backgroundColor: filters.status === "APPROVED" ? "#4CAF50" : undefined,
                        borderColor: filters.status === "APPROVED" ? "#4CAF50" : undefined,
                        color: filters.status === "APPROVED" ? "white" : undefined,
                        minWidth: "120px"
                    }}
                >
                    Approved ({counts.APPROVED})
                </Button>
                <Button
                    type={filters.status === "REJECTED" ? "primary" : "default"}
                    onClick={() => setFilters(prev => ({ ...prev, status: "REJECTED" }))}
                    style={{
                        marginRight: "10px",
                        backgroundColor: filters.status === "REJECTED" ? "#f5222d" : undefined,
                        borderColor: filters.status === "REJECTED" ? "#f5222d" : undefined,
                        color: filters.status === "REJECTED" ? "white" : undefined,
                        minWidth: "120px"
                    }}
                >
                    Rejected ({counts.REJECTED})
                </Button>
            </div>

            {/* Search and Export Bar */}
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "20px" }}>
                <Input
                    placeholder="Search by TL Name..."
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    style={{ width: "300px" }}
                    suffix={<SearchOutlined />}
                    allowClear
                />

                <Button
                    type="primary"
                    icon={<DownloadOutlined />}
                    onClick={handleExportToExcel}
                    style={{ backgroundColor: "#217346", borderColor: "#217346" }}
                >
                    Export Excel
                </Button>
            </div>

            {/* Table */}
            <Table
                columns={columns}
                dataSource={filteredData}
                rowKey="id"
                loading={loading}
                pagination={{ pageSize: 10 }}
                components={{
                    header: {
                        cell: (props) => (
                            <th
                                {...props}
                                style={{
                                    backgroundColor: "#0e497b",
                                    color: "white",
                                    whiteSpace: "nowrap",
                                }}
                            >
                                {props.children}
                            </th>
                        ),
                    },
                }}
                bordered
            />

            {/* Modals */}
            <Modal
                title="Approve TL Transfer Request"
                open={isApproveModalVisible}
                onOk={handleApprove}
                onCancel={() => setIsApproveModalVisible(false)}
                okText="Approve"
                cancelText="Cancel"
            >
                <p>Are you sure you want to approve this TL transfer request?</p>
                <Input.TextArea
                    rows={4}
                    placeholder="Enter approval remarks (optional)"
                    value={adminRemarks}
                    onChange={(e) => setAdminRemarks(e.target.value)}
                />
            </Modal>

            <Modal
                title="Reject TL Transfer Request"
                open={isRejectModalVisible}
                onOk={handleReject}
                onCancel={() => setIsRejectModalVisible(false)}
                okText="Reject"
                cancelText="Cancel"
            >
                <p>Are you sure you want to reject this TL transfer request?</p>
                <Input.TextArea
                    rows={4}
                    placeholder="Enter rejection reason"
                    value={adminRemarks}
                    onChange={(e) => setAdminRemarks(e.target.value)}
                />
            </Modal>

            {/* Transferred Employees Modal */}
            <Modal
                title={`Transferred Employees ${subsRecord ? `(${subsRecord.total_count ?? subsRecord.target_employees?.length ?? 0})` : ""}`}
                open={isSubsModalVisible}
                onCancel={() => {
                    setIsSubsModalVisible(false);
                    setSubsRecord(null);
                }}
                footer={
                    <Button onClick={() => { setIsSubsModalVisible(false); setSubsRecord(null); }}>
                        Close
                    </Button>
                }
            >
                {subsRecord?.target_employees?.length > 0 ? (
                    <div style={{ display: "flex", flexDirection: "column", gap: "8px", padding: "8px 0" }}>
                        {subsRecord.target_employees.map((emp, i) => (
                            <div
                                key={i}
                                style={{
                                    background: "#f8fafd",
                                    border: "1px solid #dde3ed",
                                    borderRadius: 8,
                                    padding: "10px 14px",
                                }}
                            >
                                <div style={{ fontWeight: 600, fontSize: 13 }}>{emp.first_name || ''} {emp.last_name || ''}</div>
                                <div style={{ fontSize: 12, color: "#8fa3bc" }}>{emp.emp_code}</div>
                            </div>
                        ))}
                    </div>
                ) : (
                    <p style={{ color: "#8fa3bc", textAlign: "center" }}>No employees listed.</p>
                )}
            </Modal>
        </>
    );
}
